import React from "react";
import { motion } from "framer-motion";
import {
  Hammer,
  LayoutTemplate,
  SquareStack,
  Layers,
  Bath,
  Building2,
  Trash2,
  ArrowUpRight,
} from "lucide-react";

const ABSTRACT_IMG =
  "https://static.prod-images.emergentagent.com/jobs/89f2f8d9-74f4-4335-b690-b9a815d801c5/images/9fc51d1be4c4e7e60520a1cfcc344ee397741df8fd81d5a14eda48d5c34b346b.png";

const SERVICES = [
  {
    key: "soft-strip",
    icon: Hammer,
    title: "Soft Strip Demolition",
    desc: "Non-structural removal of interior fittings, fixtures and finishes — ready for refurb.",
    span: "md:col-span-4 md:row-span-2",
    variant: "feature",
  },
  { key: "partitions", icon: LayoutTemplate, title: "Partition & Drywall Removal", desc: "Stud walls, plasterboard and suspended partitions removed cleanly.", span: "md:col-span-4" },
  { key: "ceiling", icon: SquareStack, title: "Ceiling Strip-Out", desc: "Suspended, plasterboard and MF ceilings dismantled and cleared.", span: "md:col-span-4" },
  { key: "floor", icon: Layers, title: "Floor Removal", desc: "Screed, tile, vinyl, carpet and timber floor coverings lifted safely.", span: "md:col-span-4" },
  { key: "kitchen-bath", icon: Bath, title: "Kitchen & Bathroom Removal", desc: "Full kitchen/bathroom strip-outs with plumbing isolation and waste disposal.", span: "md:col-span-4" },
  { key: "commercial", icon: Building2, title: "Commercial Strip-Outs", desc: "Offices, retail and hospitality — delivered out-of-hours to protect trade.", span: "md:col-span-4" },
  { key: "waste", icon: Trash2, title: "Waste Removal & Disposal", desc: "Licensed waste carriers. Full chain-of-custody and site clearance.", span: "md:col-span-8" },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-white text-[#2B2420] py-20 md:py-28"
    >
      <div className="px-6 md:px-10 lg:px-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-[#B8732C] mb-5">
              <span className="brand-gold-divider" /> Services · Stage 01
            </div>
            <h2
              className="font-black tracking-tighter leading-[0.95] text-4xl sm:text-5xl md:text-6xl max-w-3xl"
              style={{ fontFamily: "Cabinet Grotesk" }}
            >
              Every surface, fixture <br /> and finish — removed.
            </h2>
          </div>
          <p className="max-w-md text-[#8A7F74] text-base leading-relaxed">
            From a single flat to an entire commercial floorplate, our London
            crews deliver full soft-strip and demolition works with precision.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 border-t border-l border-[#E5DDD0]">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            const isFeature = s.variant === "feature";
            return (
              <motion.a
                href="#quote"
                key={s.key}
                data-testid={`service-card-${s.key}`}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className={`${s.span} col-span-1 relative group border-r border-b border-[#E5DDD0] overflow-hidden ${
                  isFeature ? "bg-[#2B2420] text-white" : "bg-white hover:bg-[#F5EFE5]"
                } transition-colors`}
              >
                {isFeature && (
                  <div
                    className="absolute inset-0 bg-cover bg-center opacity-35 group-hover:opacity-50 transition-opacity"
                    style={{ backgroundImage: `url(${ABSTRACT_IMG})` }}
                    aria-hidden
                  />
                )}
                <div className={`relative z-10 p-7 md:p-9 h-full flex flex-col justify-between min-h-[240px] ${isFeature ? "md:min-h-[520px]" : ""}`}>
                  <div className="flex items-start justify-between">
                    <Icon className={`w-7 h-7 ${isFeature ? "text-[#C9A24C]" : "text-[#B8732C]"}`} strokeWidth={1.5} />
                    <ArrowUpRight className={`w-5 h-5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${isFeature ? "text-white/70" : "text-[#8A7F74]"}`} />
                  </div>
                  <div className="mt-8">
                    <h3
                      className={`font-black tracking-tight leading-[1.05] ${isFeature ? "text-3xl md:text-4xl lg:text-5xl text-white" : "text-xl md:text-2xl text-[#2B2420]"}`}
                      style={{ fontFamily: "Cabinet Grotesk" }}
                    >
                      {s.title}
                    </h3>
                    <p className={`mt-3 text-sm leading-relaxed ${isFeature ? "text-white/80 max-w-sm" : "text-[#8A7F74]"}`}>
                      {s.desc}
                    </p>
                  </div>
                </div>
              </motion.a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
